import { useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useIntl } from 'react-intl';

import { changePassword } from 'api/r2m';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FieldErrors {
  currentPassword?: string;
  newPassword?: string;
  newPasswordRepeat?: string;
}

export default function ChangePasswordDialog({ open, onClose }: Props) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const t = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordRepeat, setNewPasswordRepeat] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setNewPasswordRepeat('');
    setErrors({});
    setGeneralError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const validate = (): FieldErrors => {
    const e: FieldErrors = {};
    if (!currentPassword.trim()) {
      e.currentPassword = t('changePassword.error.currentRequired', 'Add meg a jelenlegi jelszavadat.');
    }
    if (newPassword.length < 8) {
      e.newPassword = t('changePassword.error.tooShort', 'Az új jelszó legalább 8 karakter legyen.');
    }
    if (newPassword !== newPasswordRepeat) {
      e.newPasswordRepeat = t('changePassword.error.mismatch', 'A két új jelszó nem egyezik.');
    }
    if (newPassword && currentPassword === newPassword) {
      e.newPassword = t('changePassword.error.same', 'Az új jelszó nem egyezhet meg a jelenlegivel.');
    }
    return e;
  };

  const handleSubmit = async () => {
    const v = validate();
    setErrors(v);
    setGeneralError(null);
    if (Object.keys(v).length > 0) return;
    setSubmitting(true);
    try {
      await changePassword({ currentPassword, newPassword });
      dispatch(
        openSnackbar({
          open: true,
          message: t('changePassword.success', 'A jelszó sikeresen megváltozott.'),
          variant: 'alert',
          alert: { variant: 'filled' },
          severity: 'success'
        })
      );
      reset();
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const apiErrors: Array<{ field?: string; message?: string }> = err?.response?.data?.errors ?? [];
      if (status === 422 && apiErrors.length > 0) {
        const next: FieldErrors = {};
        for (const e of apiErrors) {
          if (e.field === 'currentPassword') next.currentPassword = e.message ?? 'Hibás jelszó.';
          if (e.field === 'newPassword') next.newPassword = e.message ?? 'Hibás új jelszó.';
        }
        setErrors(next);
        if (Object.keys(next).length === 0) {
          setGeneralError(err?.response?.data?.message ?? 'Érvénytelen kérés.');
        }
      } else {
        setGeneralError(
          err?.response?.data?.message ??
            err?.message ??
            t('changePassword.error.generic', 'Nem sikerült a jelszót módosítani.')
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('changePassword.title', 'Jelszó módosítása')}</DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 2, mt: 1 }}>
          {generalError && <Alert severity="error">{generalError}</Alert>}
          <TextField
            label={t('changePassword.current', 'Jelenlegi jelszó')}
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            error={Boolean(errors.currentPassword)}
            helperText={errors.currentPassword}
            autoFocus
            fullWidth
          />
          <TextField
            label={t('changePassword.new', 'Új jelszó')}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={Boolean(errors.newPassword)}
            helperText={errors.newPassword ?? t('changePassword.minLengthHint', 'Legalább 8 karakter.')}
            fullWidth
          />
          <TextField
            label={t('changePassword.repeat', 'Új jelszó megerősítése')}
            type="password"
            value={newPasswordRepeat}
            onChange={(e) => setNewPasswordRepeat(e.target.value)}
            error={Boolean(errors.newPasswordRepeat)}
            helperText={errors.newPasswordRepeat}
            fullWidth
          />
          <Typography variant="caption" color="text.secondary">
            {t('changePassword.staysLoggedIn', 'A művelet után érvényben marad a bejelentkezésed.')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('common.cancel', 'Mégse')}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {t('common.save', 'Mentés')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
